import { useTranslation } from "next-i18next";

import { useProductUsShippingDetails } from "@/Apollo/hooks/product/useProductUsShippingDetails";
import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { ProductDeliveryOptionItemListSkeleton } from "@/features/product-delivery-options/components/ProductDeliveryOptionItemListSkeleton";
import { UsDeliveryFacts } from "@/features/us-delivery/components/UsDeliveryFacts";
import { UsDeliveryPrice } from "@/features/us-delivery/components/UsDeliveryPrice";
import {
  UsPostalUnsupported,
  UsPostalView,
  getUsPostalView,
  showsUsDeliveryFacts,
} from "@/features/us-delivery/us-postal.utils";
import { InfoPanel } from "@/ui/InfoPanel";
import { Flavor } from "@/ui/typography/Flavor";

/**
 * The US as a whole, for every viewer, signed in or not: the gateway quotes Sendcloud with no ZIP,
 * state or street (WHO-6587). Checkout quotes the buyer's own address.
 */
const US_DELIVERY_ADDRESS = { countryCode: "US" };

type UsPostalDeliveryProps = {
  productId: string;
};

/**
 * WHO-6509. A postal listing's delivery on `/en-us`: Sendcloud's US rate (WHO-6587) as an
 * estimate, shown like every US delivery price, or why there is none (WHO-6640), then the
 * delivery time and duties note while the item can go at all.
 *
 * Asked for a US address with the unsupported options included, in a query of its own: the
 * European delivery list keeps exactly the variables and cache it had. Asked again on every visit
 * (`cache-and-network`), so "try again later" means the next visit, not the next session.
 */
export const UsPostalDelivery = ({ productId }: UsPostalDeliveryProps) => {
  const { t } = useTranslation();

  const { data, loading, error } = useProductUsShippingDetails({
    variables: {
      input: {
        product: { id: productId },
        deliveryAddress: { address: US_DELIVERY_ADDRESS },
        includeUnsupported: true,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const pending = loading && !data;

  /* A failed call reads like a rate the carrier didn't give this time. */
  const view: UsPostalView = error
    ? { kind: "unsupported", reason: "quoteUnavailable" }
    : getUsPostalView(data?.getProductShippingDetailsV2.shippingOptions);

  const unsupportedCopy: Record<UsPostalUnsupported, string> = {
    countryUnsupported: t("pdp:pdp-us-delivery-unsupported-country", {
      defaultValue: "We don't deliver this item to the US.",
    }),
    originUnsupported: t("pdp:pdp-us-delivery-unsupported-origin", {
      defaultValue:
        "This item can't be sent to the US from the seller's country yet.",
    }),
    quoteUnavailable: t("pdp:pdp-us-delivery-unsupported-quote", {
      defaultValue:
        "We can't calculate the shipping cost to the US right now. Please try again later.",
    }),
    notByPost: t("pdp:pdp-us-delivery-unsupported-parcel", {
      defaultValue: "This item can't be sent to the US by post.",
    }),
  };

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="pdp.us-delivery.postal"
      data-us-postal-view={
        pending
          ? "loading"
          : view.kind === "unsupported"
          ? view.reason
          : view.kind
      }
    >
      {/* Read out when the rate or the reason replaces the skeleton. */}
      <div aria-live="polite" aria-busy={pending}>
        {pending && <ProductDeliveryOptionItemListSkeleton />}
        {/*
          An estimate (Andrew, 4 Nov): the page asks for the US as a whole, and checkout quotes the
          buyer's own address again, which can differ by a few euros.
        */}
        {!pending && view.kind === "rate" && (
          <div className="flex flex-col gap-1">
            <UsDeliveryPrice
              label={t("pdp:pdp-us-delivery-postal-label-estimated", {
                defaultValue: "Delivery by post, estimated",
              })}
              price={view.price}
              estimate
              data-testid="pdp.us-delivery.postal-rate"
            />
            <Flavor
              size="small"
              className="text-grey-700"
              data-testid="pdp.us-delivery.postal-estimate-note"
            >
              {t("pdp:pdp-us-delivery-postal-estimate-note", {
                defaultValue:
                  "The final shipping cost is calculated at checkout for your address.",
              })}
            </Flavor>
          </div>
        )}
        {!pending && view.kind === "unsupported" && (
          <InfoPanel
            className="w-full"
            icon={<IconStateInformation className="size-5 self-start" />}
            body={<Flavor size="medium">{unsupportedCopy[view.reason]}</Flavor>}
          />
        )}
      </div>
      {!pending && showsUsDeliveryFacts(view) && <UsDeliveryFacts />}
    </div>
  );
};
